// =====================================================================
// EGL v12 Frontend - CERTIFICATE TYPES & INTERFACES
// Phase 3: Complete certificate type definitions with all new fields
// =====================================================================

// Certificate status enum
export type CertificateStatus = 'DRAFT' | 'PUBLISHED';

// Validation status enum
export type ValidationStatus = 'VALID' | 'INVALID' | 'PENDING';

// Gemstone growth origin enum (Phase 1)
export type GemstoneGrowthOrigin = 'NATURAL' | 'LAB GROWN' | 'NATURAL & LAB GROWN';

// Metal composition enum
export type MetalComposition = 
  | '9K WHITE GOLD'
  | '9K YELLOW GOLD'
  | '9K ROSE GOLD'
  | '10K WHITE GOLD'
  | '10K YELLOW GOLD'
  | '10K ROSE GOLD'
  | '14K WHITE GOLD'
  | '14K YELLOW GOLD'
  | '14K ROSE GOLD'
  | '18K WHITE GOLD' 
  | '18K YELLOW GOLD' 
  | '18K ROSE GOLD' 
  | 'PLATINUM 950';

// Gemstone types
export type GemstoneType = 
  | 'DIAMOND_1' | 'DIAMOND_2' | 'DIAMOND_3' | 'RUBY' | 'SAPPHIRE' | 'EMERALD' | 'AMETHYST'
  | 'CITRINE' | 'GARNET' | 'PERIDOT' | 'TOPAZ' | 'AQUAMARINE'
  | 'TOURMALINE' | 'ZIRCON' | 'OPAL' | 'JADE' | 'ONYX'
  | 'MOONSTONE' | 'LABRADORITE' | 'TANZANITE' | 'SPINEL'
  | 'ALEXANDRITE' | 'MORGANITE' | 'IOLITE' | 'HESSONITE'
  | 'LEMON QUARTZ' | 'RODALITE' | 'TURQUOISE' | 'FRESHWATER PEARL(S)'
  | 'SOUTH SEA PEARL(S)';

// Complete Certificate interface (40+ fields)
export interface Certificate {
  // Core fields
  id: string;
  cert_id: string;
  user_id: string;
  
  // Gemstone specifications - now supports multiple gemstones
  gemstones: Array<{
    gemstone_type: GemstoneType;
    abbreviation: string;
    description?: string;
    display_order: number;
  }>;
  gemstone_gross_weight: number;
  gemstone_color_range: string;
  gemstone_clarity_range: string;
  gemstone_growth_origin: GemstoneGrowthOrigin; // Phase 1 - Mandatory
  
  // Metal & physical properties
  metal_composition: MetalComposition;
  item_gross_weight: number;
  
  // Status & workflow
  status: CertificateStatus;
  validation_status: ValidationStatus;
  
  // Design & media
  card_design_code: string; // Mandatory
  image_url?: string;
  image_filename?: string;
  image_size_bytes?: number;
  
  // Phase 1 - New fields
  uurlid?: string; // 8-character public URL ID
  original_filename?: string; // Original upload filename
  converted_filename?: string; // S3 converted filename
  image_mime_type?: string; // image/jpeg or image/webp
  image_upload_time?: string; // ISO timestamp
  
  // Timeline
  created_at: string;
  updated_at: string;
  published_at?: string;
  
  // Comment lines (Phase 1)
  comment_line_1?: string;
  comment_line_2?: string;
  comment_line_3?: string;
  
  // Abbreviation lines (Phase 1)
  abbreviation_line_1?: string;
  abbreviation_line_2?: string;
  abbreviation_line_3?: string;
  abbreviation_line_4?: string;
  abbreviation_line_5?: string;
  abbreviation_line_6?: string;
  abbreviation_line_7?: string;
  abbreviation_line_8?: string;
  abbreviation_line_9?: string;
  abbreviation_line_10?: string;
  abbreviation_line_11?: string;
  abbreviation_line_12?: string;
  abbreviation_line_13?: string;
  
  // User information (from joins)
  created_by_name?: string;
  created_by_username?: string;
  user_location?: string;
}

// Certificate creation form data
// Enhanced gemstone selection with measurement system
export interface GemstoneSelection {
  gemstone_type: string;
  growth_origin?: 'NATURAL' | 'LAB GROWN' | null; // Individual growth origin (null for pearls, required for others)
  measurement_type?: 'WEIGHT' | 'DIAMETER' | 'DIAMETER_RANGE';
  measurement_value?: string; // Single value (weight in carats or diameter in mm)
  measurement_range?: string; // Range value for pearls (e.g., "2.25-3.75")
  measurement_unit?: 'carats' | 'mm';
  carat_weight?: string; // Backward compatibility
  
  // Diamond-specific fields (optional - only for DIAMOND_X gemstone_type)
  diamond_color_type?: 'FIXED' | 'RANGE';
  diamond_color_fixed?: string; // Single color (D, E, F, etc.)
  diamond_color_range?: string; // Color range (D-F, G-H, etc.)
  diamond_clarity_type?: 'FIXED' | 'RANGE';
  diamond_clarity_fixed?: string; // Single clarity (FL, IF, VVS1, etc.)
  diamond_clarity_range?: string; // Clarity range (FL-IF, VVS1-VVS2, etc.)
}

// Gemstone configuration from database
export interface GemstoneConfig {
  gemstone_type: string;
  abbreviation: string;
  default_measurement_type: 'WEIGHT' | 'DIAMETER';
  default_measurement_unit: 'carats' | 'mm';
  supports_ranges: boolean;
  min_value: number;
  max_value: number;
  is_active: boolean;
}

export interface CertificateFormData {
  // Mandatory fields
  cert_id: string;
  cert_id_confirm?: string; // Confirmation field for cert_id (frontend only)
  gemstone_selections: GemstoneSelection[]; // Enhanced with measurement system
  metal_composition: MetalComposition;
  item_gross_weight: string; // String for form input, converted to number on submit
  gemstone_growth_origin: GemstoneGrowthOrigin;
  card_design_code: string;

  // Legacy fields used in validation
  gemstone_type?: string;
  gemstone_weight?: number;
  total_weight?: number;

  // Optional fields
  gemstone_color_range?: string;
  gemstone_clarity_range?: string;
  
  // Comment lines
  comment_line_1?: string;
  comment_line_2?: string;
  comment_line_3?: string;
  
  // Abbreviation lines
  abbreviation_line_1?: string;
  abbreviation_line_2?: string;
  abbreviation_line_3?: string;
  abbreviation_line_4?: string;
  abbreviation_line_5?: string;
  abbreviation_line_6?: string;
  abbreviation_line_7?: string;
  abbreviation_line_8?: string;
  abbreviation_line_9?: string;
  abbreviation_line_10?: string;
  abbreviation_line_11?: string;
  abbreviation_line_12?: string;
  abbreviation_line_13?: string;
  
  // Image fields
  image_url?: string | null;
  image_filename?: string | null;
  image_size_bytes?: number | null;
  original_filename?: string | null;
}

// Certificate ID validation response (Phase 2)
export interface CertificateIdValidation {
  valid: boolean;
  available: boolean;
  message: string;
  format_valid: boolean;
  duplicate_check: boolean;
  cert_id?: string;
}

// Public certificate response (Phase 2)
export interface PublicCertificateResponse {
  success: boolean;
  message: string;
  data?: {
    certificate: {
      cert_id: string;
      uurlid: string;
      gemstones: Array<{
        gemstone_type: GemstoneType;
        abbreviation: string;
        description?: string;
        display_order: number;
      }>;
      gemstone_gross_weight: number;
      gemstone_color_range: string;
      gemstone_clarity_range: string;
      gemstone_growth_origin: GemstoneGrowthOrigin;
      metal_composition: MetalComposition;
      item_gross_weight: number;
      card_design: string;
      image_url?: string;
      published_at?: string;
      comment_lines: string[];
      abbreviation_lines: string[];
    };
    public_url: string;
    verification_status: string;
  };
  error?: string;
}

// Card design interface
export interface CardDesign {
  design_code: string;
  design_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Form validation state
export interface FormValidationState {
  cert_id: {
    valid: boolean;
    available: boolean;
    checking: boolean;
    message: string;
  };
  username: {
    valid: boolean;
    checking: boolean;
    message: string;
  };
  form_complete: boolean;
  can_submit: boolean;
}

// Progressive form reveal state
export interface FormRevealState {
  show_card_design: boolean;
  show_cert_id: boolean;
  show_main_fields: boolean;
  show_image_upload: boolean;
  show_save_button: boolean;
  show_public_url: boolean;
}

// API response interfaces
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  timestamp?: string;
}

export interface PaginatedResponse<T = any> {
  success: boolean;
  message: string;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  error?: string;
  timestamp?: string;
}

// Search and filter interfaces
export interface CertificateSearchParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  query?: string;
  status?: CertificateStatus;
  validation_status?: ValidationStatus;
  date_from?: string;
  date_to?: string;
  created_by?: string;
  gemstone_type?: GemstoneType;
  metal_composition?: MetalComposition;
  gemstone_growth_origin?: GemstoneGrowthOrigin; // Phase 1
}

// Constants for validation
export const CERTIFICATE_CONSTANTS = {
  CERT_ID_MAX_LENGTH: 15,
  CERT_ID_PATTERN: /^[A-Z0-9]{1,15}$/,
  UURLID_LENGTH: 8,
  UURLID_PATTERN: /^[A-Z0-9]{8}$/,
  COMMENT_MAX_LENGTH: 65,
  ABBREVIATION_MAX_LENGTH: 31,
  ASCII_PATTERN: /^[\x20-\x7E]*$/,
  MAX_IMAGE_SIZE: 512000, // 512KB
  ALLOWED_MIME_TYPES: ['image/jpeg', 'image/webp'],
  DEFAULT_COLOR_RANGE: 'G - H',
  DEFAULT_CLARITY_RANGE: 'VS - SI',
  DEFAULT_CARD_DESIGN: 'EUROPEANGEMOLOGICALLAB'
} as const;

// All types are already exported above with their declarations 